import { useState } from 'react';
import { Kart, KartBaslik, Rozet, UyariSerit } from '../../bilesenler/temel.js';
import { Simge } from '../../bilesenler/Simge.js';
import { api, useEylem, useVeri } from '../../lib/veri.js';
import { useOturum } from '../../lib/oturum.js';

/**
 * KAYITLI LOG ANALİZLERİ
 *
 * Personel bir logu inceleyip "iş emrine kaydet" diyordu ama kaydettiği
 * analiz HİÇBİR YERDE görünmüyordu: kayıt veritabanına yazılıyor, sonra
 * kaybediliyordu. Bu kart onları geri getiriyor — iş emrinde o işin
 * analizleri, müşteri sayfasında aracın bütün geçmişi.
 *
 * Aynı bileşen iki yerde kullanılır; tek fark hangi filtreyle sorulduğu.
 */

interface AnalizKaydi {
  id: number;
  created_at: string;
  baslik: string;
  kaynak: string;
  saglik: number | null;
  ozet: string;
  bulgular: string | unknown[];
  /* Ham logun dosya kaydı. Sunucu bu bağın analizle AYNI iş emrine/araca
     ait olduğunu doğrular, bu yüzden buradaki indirme bağlantısı başka bir
     müşterinin dosyasına açılamaz. Eski kayıtlarda yoktur. */
  dosya_id?: number | null;
}

interface BulguSatiri { sev?: string; title?: string; body?: string }

/** Bulgular sütunu JSON metin olarak saklanıyor; iki biçimi de kabul et. */
function bulgulariCoz(x: AnalizKaydi['bulgular']): BulguSatiri[] {
  if (Array.isArray(x)) return x as BulguSatiri[];
  try {
    const c = JSON.parse(String(x || '[]')) as unknown;
    return Array.isArray(c) ? (c as BulguSatiri[]) : [];
  } catch { return []; }
}

function sayilar(bulgular: BulguSatiri[]) {
  return {
    kritik: bulgular.filter((b) => b.sev === 'crit').length,
    dikkat: bulgular.filter((b) => b.sev === 'warn').length,
  };
}

/** Sunucudan gelen tarihi yerel saatle, kısa biçimde gösterir. */
function tarihGoster(s: string): string {
  if (!s) return '—';
  const d = new Date(s.includes('T') ? s : s.replace(' ', 'T') + 'Z');
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString('tr-TR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function LogAnalizKarti(
  { isEmriId, aracId, baslik = 'Log analizleri' }:
  { isEmriId?: number; aracId?: number; baslik?: string },
) {
  const { yetkili } = useOturum();
  const [acik, setAcik] = useState<number | null>(null);

  const sorgu = isEmriId ? `is_emri_id=${isEmriId}` : (aracId ? `arac_id=${aracId}` : '');
  const liste = useVeri<AnalizKaydi[]>(
    yetkili('log.kullan') && sorgu ? `/log-analizleri?${sorgu}` : null,
  );

  const sil = useEylem(async (id: number) => {
    await api.sil(`/log-analizleri/${id}`);
    liste.yenile();
  });

  /* İzni olmayana boş bir kart göstermek yerine hiç gösterme. */
  if (!yetkili('log.kullan') || !sorgu) return null;
  const kayitlar = liste.veri ?? [];
  if (!liste.yukleniyor && kayitlar.length === 0) return null;

  return (
    <Kart>
      <KartBaslik baslik={baslik}>
        <span style={{ fontSize: 'var(--p-12)', color: 'var(--metin-3)' }}>
          {kayitlar.length} kayıt
        </span>
      </KartBaslik>
      {sil.hata && <div className="kart-govde"><UyariSerit ton="kritik">{sil.hata}</UyariSerit></div>}
      <div className="tablo-sarma">
        <table className="tablo">
          <thead>
            <tr>
              <th>Tarih</th><th>Başlık</th><th>Kaynak</th>
              <th className="sag">Sağlık</th><th className="sag">Bulgu</th>
              <th className="dar" />
              {yetkili('log.kullan') && <th className="dar" />}
            </tr>
          </thead>
          <tbody>
            {kayitlar.map((k) => {
              const b = bulgulariCoz(k.bulgular);
              const s = sayilar(b);
              return (
                <tr key={k.id}>
                  <td className="ikincil dar">{tarihGoster(k.created_at)}</td>
                  <td>{k.baslik}</td>
                  <td className="ikincil">{k.kaynak || '—'}</td>
                  <td className="sag">
                    {/* Motor değerlendirme yapamadığında sağlık boş gelir;
                        o zaman puan uydurmak yerine "—" gösterilir. */}
                    {k.saglik == null ? '—' : (
                      <Rozet ton={k.saglik >= 85 ? 'olumlu' : (k.saglik >= 60 ? 'uyari' : 'kritik')}>
                        {k.saglik}
                      </Rozet>
                    )}
                  </td>
                  <td className="sag ikincil">
                    {s.kritik > 0 && <span style={{ color: 'var(--kritik)' }}>{s.kritik} kritik</span>}
                    {s.kritik > 0 && s.dikkat > 0 && ' · '}
                    {s.dikkat > 0 && <span style={{ color: 'var(--uyari)' }}>{s.dikkat} dikkat</span>}
                    {s.kritik === 0 && s.dikkat === 0 && '—'}
                  </td>
                  <td className="sag">
                    <button
                      className="btn btn-sade btn-kucuk"
                      onClick={() => setAcik(acik === k.id ? null : k.id)}
                      aria-expanded={acik === k.id}
                    >
                      {acik === k.id ? 'Gizle' : 'Bulgular'}
                    </button>
                    {/* HAM LOG. Kaydı üreten dosya iş emrine yüklendiyse
                        buradan indirilir ve aynı logla yeni bir analiz
                        açılabilir. Dosyası olmayan eski kayıtlarda düğme
                        hiç çıkmaz — ölü bağlantı bırakmak, hiç bağlantı
                        olmamasından kötüdür. */}
                    {k.dosya_id != null && (
                      <>
                        <a
                          className="btn btn-sade btn-kucuk"
                          href={`/api/dosyalar/${k.dosya_id}/indir`}
                          download
                          title="Bu analizin ham log dosyasını indir"
                        >
                          <Simge ad="indir" boyut={14} /> Ham logu indir
                        </a>
                        {/* Analiz KAYDININ kimliği taşınır, yalnız dosya
                            değil: Log Analiz ekranı böylece hem dosyayı hem
                            de kaydın yazılım seviyesini alır ve rapor ilk
                            günkü ayarlarla açılır. */}
                        <a
                          className="btn btn-sade btn-kucuk"
                          href={`/log-analiz?analiz=${k.id}${isEmriId ? `&is_emri=${isEmriId}` : ''}`}
                          title="Aynı logu Log Analiz ekranında yeniden aç"
                        >
                          <Simge ad="yenile" boyut={14} /> Bu logla yeniden analiz et
                        </a>
                      </>
                    )}
                  </td>
                  <td className="sag">
                    <button
                      className="btn btn-sade btn-kucuk"
                      disabled={sil.calisiyor}
                      onClick={() => { void sil.yurut(k.id); }}
                      title="Analizi çöpe taşı"
                    >
                      <Simge ad="cop" className="simge" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {acik != null && (() => {
        const k = kayitlar.find((x) => x.id === acik);
        if (!k) return null;
        const b = bulgulariCoz(k.bulgular);
        return (
          <div className="kart-govde sutun" style={{ gap: 'var(--b3)' }}>
            {k.ozet && (
              <div style={{ fontSize: 'var(--p-13)', color: 'var(--metin-2)' }}>{k.ozet}</div>
            )}
            {b.length === 0 ? (
              <div style={{ fontSize: 'var(--p-13)', color: 'var(--metin-3)' }}>
                Bu kayıtta ayrıntılı bulgu saklanmamış.
              </div>
            ) : b.map((x, i) => (
              <div key={i} style={{ fontSize: 'var(--p-13)' }}>
                <Rozet ton={x.sev === 'crit' ? 'kritik' : (x.sev === 'warn' ? 'uyari' : 'notr')} sade>
                  {x.sev === 'crit' ? 'Kritik' : (x.sev === 'warn' ? 'Dikkat' : 'Bilgi')}
                </Rozet>{' '}
                <strong>{x.title}</strong>
                {x.body && <div style={{ color: 'var(--metin-2)' }}>{x.body}</div>}
              </div>
            ))}
          </div>
        );
      })()}
    </Kart>
  );
}
